import React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useRouteParams } from '@/context/routeParamsContext';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { cardArtMapping } from '@/constants/productMapping';
import { getCountryCode, getCardArtUrl } from '@/helpers/utils';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useModal } from '@/context/modalContext';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useSelectedProductFromUserWallet } from '@/hooks/memberHooks';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { removeFiltersQuery } from '@/helpers/airportFiltersHelper';
import {
  IconChevronDown,
  IconMenu,
  IconClose,
} from '@americanexpress/dls-icons';
import {
  TclMigrationFeatures,
  useTCLFeatureFlags,
} from '@/hooks/featureFlagHooks';
import { RouteParams } from '@/types';
import styles from './GlobalHeader.module.scss';
import touchTargetStyles from '../TargetTouchSize.module.scss';
import LoungeFinderBreadcrumb from './LoungeFinderBreadcrumb';
import AirportsSearchBox from '../AirportsSearchBox';
import LFLink from '../LFLink';
import { modalTypes } from '../modals/constants';
import LoungeAccessProductDescription from '../LoungeAccessProductDescription';
import CenturionNetworkNav from './CenturionNetworkTab';

type GlobalHeaderProps = {
  location: {
    search: string;
  };
};

type WalletProduct = {
  displayAccountNumber?: string;
  cardArtUrl?: string;
};

const GlobalHeader: React.FC<GlobalHeaderProps> = ({ location }) => {
  const { product, loungeId, airportCode, pathname } =
    useRouteParams() as RouteParams;

  const isCenturionNetworkPage = pathname.includes('centurion-network');
  const isCulinaryCollectivePageEnabled = useTCLFeatureFlags(
    TclMigrationFeatures.CULINARY_COLLECTIVE_PAGE
  );

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isTCNMenuOpen, setIsTCNMenuOpen] = React.useState(false);
  const [isFindAirportMenuOpen, setIsFindAirportMenuOpen] =
    React.useState(false);
  const tcnButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const findAirportButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const [activeTabIndicatorStyle, setActiveTabIndicatorStyle] = React.useState({
    width: 0,
    transform: 'translateX(0px)',
    opacity: 0,
  });

  React.useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prevState) => !prevState);
  };

  const toggleTCNMenu = () => {
    setIsFindAirportMenuOpen(false);
    setIsTCNMenuOpen((prevState) => !prevState);
  };

  const toggleFindAirportMenu = () => {
    setIsTCNMenuOpen(false);
    setIsFindAirportMenuOpen((prevState) => !prevState);
  };

  React.useEffect(() => {
    const activeButton = isTCNMenuOpen
      ? tcnButtonRef.current
      : isFindAirportMenuOpen
        ? findAirportButtonRef.current
        : null;

    if (!activeButton) {
      setActiveTabIndicatorStyle((prevState) => ({
        ...prevState,
        opacity: 0,
      }));
      return;
    }

    setActiveTabIndicatorStyle({
      width: activeButton.offsetWidth,
      transform: `translateX(${activeButton.offsetLeft}px)`,
      opacity: 0.5,
    });
  }, [isTCNMenuOpen, isFindAirportMenuOpen]);

  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const { locale } = useSelector(getLocaleData);
  const imgSrc = getCardArtUrl({
    baseOneCmsImgUrl,
    product: cardArtMapping[product as keyof typeof cardArtMapping],
    locale,
  });
  const { customTrack } = useAnalyticsContext();
  const { setModalName } = useModal();

  const handleToggleModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    setModalName(modalTypes.SELECT_CARD_MODAL);
    customTrack({
      event: e,
      detail: 'card selector header',
    });
  };
  const { header, productDisplayNames, selectCard } = useCommonContent();
  const productName = `${productDisplayNames[product]?.displayName} (${getCountryCode(product, locale)})`;
  const urlSpecifiedProductFromUsersWallet = useSelectedProductFromUserWallet(
    product,
    locale
  ) as WalletProduct | null;

  const getCardCalloutNotification = () => {
    if (isCenturionNetworkPage) {
      return null; // TODO: Need to handle this once we decide how to go ahead with Card Callout Notification for TCL.
    }

    return (
      <div className="tw-col-span-2 tw-flex tw-justify-end">
        <button
          type="button"
          aria-label="Card Callout Notification"
          className="tw-flex tw-items-center focus-within_tw-rounded-sm focus-within_tw-outline focus-within_tw-outline-blue-bright-600"
          onClick={handleToggleModal}
        >
          <img
            src={urlSpecifiedProductFromUsersWallet?.cardArtUrl || imgSrc}
            alt={productName ? `${productName}` : 'Card'}
            className="tw-h-6 tw-w-9dot5"
          />

          <div
            aria-label={productName}
            className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-pl-2 tw-text-left tw-text-sm tw-text-gray-100 first-child_tw-flex-1 first-child_tw-whitespace-normal first-child_tw-break-words md_tw-text-base"
          >
            <div className="tw-hidden md_tw-block">
              <LoungeAccessProductDescription
                locale={locale}
                product={product}
                customMessage={
                  urlSpecifiedProductFromUsersWallet
                    ? selectCard?.accountNumber
                    : '{productTitle} ({countryCode})'
                } // No need to localize as this is just productName and code
              />
            </div>
            <span>
              <IconChevronDown
                size="xs"
                isFilled={true}
                color="inherit"
                className="tw-fill-blue-bright-200 md_tw-fill-gray-300"
              />
            </span>
          </div>
        </button>
      </div>
    );
  };

  const getBreadCrumb = () => {
    return loungeId ? (
      <div className="tw-mx-3 tw-mx-auto tw-max-w-360 tw-px-4 md_tw-px-6 lg_tw-px-8 xl_tw-px-10">
        <div className="tw-flex tw-items-center tw-border-t tw-border-blue-deep-tint-700-t12 tw-py-2dot5">
          <LoungeFinderBreadcrumb loungeId={loungeId} />
        </div>
      </div>
    ) : null;
  };

  return !isCulinaryCollectivePageEnabled ? (
    <div className="dls-deep-blue-bg tw-relative tw-z-30">
      <div className="tw-mx-auto tw-flex tw-flex-col tw-p-3 tw-px-4 md_tw-px-6 lg_tw-max-w-360 lg_tw-px-8 xl_tw-px-10">
        <div className="tw-grid tw-grid-flow-col tw-items-center tw-gap-4 lg_tw-grid-cols-3">
          <div className="tw-py-3">
            <LFLink
              detail="logo-link"
              element="logo-link"
              to={`${product || 'the-platinum-card'}`}
              searchParam={removeFiltersQuery(location.search)}
              className="tw-inline-flex hover_tw-underline hover_tw-decoration-gray-100"
            >
              <div className="tw-flex tw-items-center tw-font-sans tw-text-base tw-leading-6 tw-text-gray-100">
                <p className="tw-break-keep tw-font-bold">{header?.title}</p>
                <span className="tw-mx-2 tw-h-5 tw-w-px tw-bg-gray-100" />
                <p className="tw-break-keep tw-font-normal">{header?.label}</p>
              </div>
            </LFLink>
          </div>
          <div>
            {airportCode && (
              <div className="tw-hidden tw-z-1-important lg_tw-block">
                <AirportsSearchBox className={styles.searchBar} />
              </div>
            )}
          </div>
          {getCardCalloutNotification()}
        </div>
        {airportCode && (
          <div className="tw-mt-3 tw-block tw-z-1-important lg_tw-hidden">
            <AirportsSearchBox className={styles.searchBar} />
          </div>
        )}
      </div>
      {getBreadCrumb()}
    </div>
  ) : (
    <div className="dls-deep-blue-bg tw-relative tw-z-30">
      <div className="tw-mx-auto tw-flex tw-flex-col tw-p-3 tw-px-4 md_tw-px-6 lg_tw-max-w-360 lg_tw-px-8 xl_tw-px-10">
        <div className="tw-grid tw-grid-cols-12 md_tw-flex md_tw-justify-between md_tw-gap-4">
          <button
            aria-label={isMobileMenuOpen ? 'Close Menu' : 'Menu'}
            type="button"
            className="tw-col-span-2 tw-flex tw-p-3 focus-within_tw-outline-none md_tw-hidden"
            onClick={toggleMobileMenu}
          >
            {isMobileMenuOpen ? (
              <IconClose
                size="md"
                color="inherit"
                className="tw-fill-blue-bright-200"
              />
            ) : (
              <IconMenu
                size="md"
                color="inherit"
                className="tw-fill-blue-bright-200"
              />
            )}
          </button>
          <div className="tw-col-span-8 tw-flex tw-justify-center md_tw-col-auto md_tw-justify-start">
            <div className="tw-relative tw-flex tw-gap-8 tw-py-3">
              <LFLink
                detail="logo-link"
                element="logo-link"
                to={`${product || 'the-platinum-card'}`}
                searchParam={removeFiltersQuery(location.search)}
                className={clsx(
                  'tw-inline-flex hover_tw-underline hover_tw-decoration-gray-100',
                  touchTargetStyles.touchTarget
                )}
              >
                <div className="tw-flex tw-items-center tw-font-sans tw-text-base tw-leading-6 tw-text-gray-100">
                  <p className="tw-break-keep tw-font-bold">{header?.title}</p>
                  <span className="tw-mx-2 tw-h-5 tw-w-px tw-bg-gray-100" />
                  <p className="tw-break-keep tw-font-normal">
                    {header?.label}
                  </p>
                </div>
              </LFLink>
              <button
                ref={tcnButtonRef}
                type="button"
                onClick={toggleTCNMenu}
                className="tw-flex tw-hidden tw-items-center tw-font-sans tw-text-base tw-leading-6 tw-text-gray-100 focus-within_tw-outline-none md_tw-block"
              >
                {header?.tcnTabTitle}
              </button>
              <button
                ref={findAirportButtonRef}
                type="button"
                onClick={toggleFindAirportMenu}
                className="tw-flex tw-hidden tw-items-center tw-font-sans tw-text-base tw-leading-6 tw-text-gray-100 focus-within_tw-outline-none md_tw-block"
              >
                {header?.searchTabTitle}
              </button>
              <span
                className="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-mb-1 tw-hidden tw-h-1 tw-rounded-lg tw-bg-white tw-transition-all tw-duration-300 tw-ease-in-out md_tw-block"
                style={activeTabIndicatorStyle}
              />
            </div>
          </div>
          {getCardCalloutNotification()}
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className="tw-z-20 tw-min-h-screen tw-bg-blue-deep-900 tw-text-sm tw-text-gray-100 md_tw-hidden">
          <CenturionNetworkNav />
        </div>
      )}
      <div
        className={clsx(
          'tw-absolute tw-left-0 tw-right-0 tw-z-30 tw-hidden tw-h-107 tw-overflow-hidden tw-bg-blue-deep-900 tw-text-sm tw-text-gray-100 tw-transition-all tw-duration-300 tw-ease-in-out md_tw-block',
          isTCNMenuOpen || isFindAirportMenuOpen ? 'tw-max-h-107' : 'tw-max-h-0'
        )}
      >
        {isTCNMenuOpen && <CenturionNetworkNav />}
        {isFindAirportMenuOpen && <div>Find a City or Airport Menu</div>}
      </div>
      {getBreadCrumb()}
    </div>
  );
};

export default GlobalHeader;
